/**
 * The queue that warms the inspector ahead of the pointer.
 *
 * The canon's inspector swaps under a moving pointer, so a read that starts when the trainer
 * arrives on a row is a read they wait for. The list's plans are therefore fetched in the
 * background, in the order the list draws them, and the row the pointer touches jumps the queue.
 *
 * ── Four at a time, in view order, and the pointed row goes FIRST ────────────────────────────
 * Owner decision, 3 September 2026. Four is the concurrency the browser gives a single origin
 * without queueing on its own, and view order is what makes the first screenful arrive before the
 * eighth row nobody has scrolled to. `promote` moves an id to the front of what has NOT started —
 * a request already in flight cannot be reordered, and the row the trainer is on has its own
 * observer, so promoting it only shortens a wait that is already happening.
 *
 * ── And a WINDOW, because the list is not eight rows long for everybody ──────────────────────
 * Owner decision, 3 September 2026. The queue used to seed itself from every row, so a trainer with
 * a hundred and twenty client plans issued a hundred and twenty full-plan reads on one screen load
 * — an unbounded fan-out driven by somebody else's data. It warms the first screenful and a little
 * past it instead.
 *
 * The window is a BUDGET rather than a shorter list: every id stays in `pending`, and `promote`
 * grants one extra start when it pulls a row forward. So a row past the window loads when the
 * trainer points at it, behind the skeleton gate the inspector already has, and pointing is the
 * only thing that widens the fan-out. Hovering a row already warmed grants nothing — it returns on
 * `started` before it reaches the budget.
 *
 * ── The seed is the UNFILTERED list, and that is what makes the budget a budget ───────────────
 * The queue keys on its list's content, and the screen used to hand it the NARROWED rows: every
 * character typed produced a different key, tore the queue down and rebuilt it with an empty
 * `started` set and a whole fresh budget. Eight characters over eight different subsets could
 * start eight rounds of the window's worth of reads, which is precisely the fan-out the window
 * exists to stop — only now driven by the trainer's own typing rather than by their row count.
 *
 * So it seeds from every row the scope holds, and the filter reaches it through `promote` and
 * nowhere else: the row the trainer points at is granted its read whether or not a phrase is
 * narrowing the list around it. The queue is therefore rebuilt on a SCOPE switch, a language
 * change and a refetch, and on nothing a keystroke does.
 *
 * ── The queue is a REF, not state, and the reason is the compiler ────────────────────────────
 * Nothing here renders. A `useState` queue would re-render the whole list on every completed
 * prefetch — eight renders of eight rows to warm a cache nobody is reading yet. The ref holds the
 * pending ids and the in-flight count; `promote` mutates it from an event handler, never during
 * render, which is the rule the React Compiler enforces.
 *
 * ── The COVERS are warmed too, on the SAME budget, and that is the same job ───────────────────
 * The canon's own note: "okładki preloadowane na mount — inaczej przy hoverze widać starą
 * grafikę". The hero is a 976px-wide photograph, and a browser that starts fetching it when the
 * pointer arrives paints the previous plan's cover until it lands. `new Image()` rather than a
 * hidden `<img>`: the request goes through the same HTTP cache the hero will read, and nothing is
 * added to the document to lay out.
 *
 * They are windowed for the reason the reads are, and it is the more expensive of the two: a hero
 * is a 280–360KB JPEG, so a trainer with a hundred and twenty client plans was pulling about
 * thirty-eight megabytes on one screen load — over the same connection pool as the thumbnails the
 * list is actually drawing. The first screenful and the lookahead are warmed on mount and the
 * pointed row's cover is warmed inside `promote`, so the cover the trainer is about to see is
 * always the one being fetched. A URL is requested once: `warmed` remembers what has been asked
 * for, because a hover repeated sixty times a second must not repeat a request.
 *
 * ── Cancellation is by GENERATION, not by aborting ───────────────────────────────────────────
 * A prefetch in flight is a request the cache will keep and the next pointed row may want, so
 * unmounting does not abort it — it stops the queue from starting anything more. Every pump reads
 * the generation it was started under and returns as soon as it no longer matches, which also
 * covers the language changing under a half-drained queue: the ids are the same but the cache
 * entries are not, so the old queue must not go on warming keys nobody will read.
 *
 * ── SIGNING OUT ends the queue, and the generation alone could not see it ────────────────────
 * `signOut` bumps `entities/session`'s epoch, cancels every query, clears the cache and only then
 * dispatches — the order is deliberate, so that a request from the previous session cannot re-seed
 * the cache after `clear()`. The queue defeated it: `cancelQueries` settles the prefetch in flight,
 * its `.finally()` drains the next ids, and those land after the cache is empty, with the outgoing
 * trainer's plans in it. A generation bumped only on UNMOUNT cannot notice, and neither can a
 * subscription to the session's status, which changes a frame later and after the clear.
 *
 * So every pump compares the EPOCH it was started under as well, read synchronously from the slice
 * that owns it. Not a second notion of "the session ended" — the same one, asked at the moment the
 * queue can still act on it.
 */

import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { sessionEpoch } from '~/entities/session';
import { useContentLanguage } from '~/shared/i18n/contentLanguage';

import { prefetchInspector } from '../api/inspectorQueries';

/** The owner's cap on concurrency. See the header. */
const MAX_IN_FLIGHT = 4;

/**
 * How many rows a cold screen warms before the trainer points at anything: the list card holds
 * about a dozen rows at the reference viewport, and four past them covers the first scroll.
 */
const WARM_WINDOW = 12;
const WARM_LOOKAHEAD = 4;
const WARM_BUDGET = WARM_WINDOW + WARM_LOOKAHEAD;

export interface InspectorPrefetch {
  /**
   * The trainer is on this row: move its plan to the head of what has not started, grant it a
   * read, and warm its cover. Safe to call on every pointer move — everything it does is idempotent.
   */
  readonly promote: (row: InspectorPrefetchRow) => void;
}

interface QueueState {
  /** Plan ids not yet started, in the order they will be fetched. */
  pending: number[];
  /** Ids already handed to `prefetchQuery`, so a promote cannot re-enqueue them. */
  started: Set<number>;
  inFlight: number;
  /** Bumped whenever the list or the language changes, and on unmount. */
  generation: number;
  /** The session this queue's requests belong to. A queue outlives its session for one drain. */
  epoch: number;
  /** How many more reads the queue may START on its own. `promote` adds one per pointed row. */
  budget: number;
}

/** What the queue needs to know about one row: which plan, and which photograph. */
export interface InspectorPrefetchRow {
  readonly id: number;
  readonly heroUrl: string;
}

/**
 * Ask the browser for one cover, once.
 *
 * `new Image()` rather than a hidden `<img>`: the request goes through the same HTTP cache the hero
 * will read, and nothing is added to the document to lay out. `warmed` is what keeps a hover — which
 * fires many times a second — from asking twice, and it outlives a generation on purpose: the
 * browser's own cache does too, so re-requesting after a scope switch would buy nothing.
 */
function warmCover(warmed: Set<string>, url: string) {
  if (url === '' || warmed.has(url)) return;
  warmed.add(url);
  const image = new Image();
  image.src = url;
}

export function useInspectorPrefetch(rows: readonly InspectorPrefetchRow[]): InspectorPrefetch {
  // The content language keys the plan's cache entry, so the queue warms what the inspector will
  // read. Taken from the store rather than from the caller — there is only one answer to it.
  const lang = useContentLanguage();
  const client = useQueryClient();
  const queue = useRef<QueueState>({
    pending: [],
    started: new Set(),
    inFlight: 0,
    generation: 0,
    epoch: sessionEpoch(),
    budget: WARM_BUDGET,
  });
  // The pump is held in a ref so `promote` can restart a drained queue without depending on the
  // effect's closure — the handler is created once and the effect re-runs on every list change.
  const pump = useRef<() => void>(() => undefined);
  // Every cover this hook has already asked the browser for. See `warmCover`.
  const warmed = useRef<Set<string>>(new Set());

  // ── The effects key on the list's CONTENT, and read the list back out of that key ───────────
  // `rows` is a new array on every render, so depending on it would restart the queue each time the
  // screen re-rendered. Joining it gives a value the dependency comparison can use — and each
  // effect then parses its own list back out of that key rather than closing over `rows`, which is
  // what keeps the dependency lists exhaustive. An `eslint-disable` here would be worse than the
  // parse: the React Compiler treats one as an opt-out and silently stops compiling the hook.
  const listKey = rows.map((row) => row.id).join(',');
  const coverKey = rows.map((row) => row.heroUrl).join('\n');

  useEffect(() => {
    const generation = queue.current.generation + 1;
    const epoch = sessionEpoch();
    queue.current = {
      pending: listKey === '' ? [] : listKey.split(',').map(Number),
      started: new Set(),
      inFlight: 0,
      generation,
      epoch,
      budget: WARM_BUDGET,
    };

    const drain = () => {
      const current = queue.current;
      if (current.generation !== generation) return;
      // Read at the moment of the pump, not closed over: the sign-out that matters happens between
      // this queue's last request being cancelled and the cache being cleared.
      if (sessionEpoch() !== epoch) return;
      while (
        current.inFlight < MAX_IN_FLIGHT &&
        current.pending.length > 0 &&
        current.budget > 0
      ) {
        const planId = current.pending.shift();
        if (planId === undefined) break;
        if (current.started.has(planId)) continue;
        current.started.add(planId);
        current.inFlight += 1;
        current.budget -= 1;
        void prefetchInspector(client, planId, lang).finally(() => {
          const after = queue.current;
          if (after.generation !== generation) return;
          after.inFlight -= 1;
          drain();
        });
      }
    };

    pump.current = drain;
    drain();

    return () => {
      // Not an abort: what is in flight is a response the cache will keep. This only stops the
      // queue from starting anything else.
      queue.current.generation += 1;
      pump.current = () => undefined;
    };
  }, [client, lang, listKey]);

  useEffect(() => {
    if (coverKey === '') return;
    // The SAME budget the plan reads get, and for a heavier reason — see the header. Everything
    // past it is warmed by `promote` when the trainer points at its row.
    for (const url of coverKey.split('\n').slice(0, WARM_BUDGET)) {
      warmCover(warmed.current, url);
    }
    // Nothing to clean up: an image request the browser has started is a cache entry either way,
    // and there is no element to detach.
  }, [coverKey]);

  return {
    promote: (row: InspectorPrefetchRow) => {
      const state = queue.current;
      if (sessionEpoch() !== state.epoch) return;
      // The cover FIRST and outside the queue's own guards: the pane the trainer is about to look
      // at paints its photograph whether or not its plan read has already been started or is past
      // the window. This is the whole of what a row beyond the cold cover budget gets.
      warmCover(warmed.current, row.heroUrl);
      if (state.started.has(row.id)) return;
      const at = state.pending.indexOf(row.id);
      if (at < 0) return;
      state.pending.splice(at, 1);
      state.pending.unshift(row.id);
      // One extra read for the row the trainer is actually on, which is what lets a row past the
      // window load at all once the cold budget is spent.
      state.budget += 1;
      pump.current();
    },
  };
}
